import { ReactNode } from 'react'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import Typography from '@mui/material/Typography'
import {
  DEFAULT_ASSISTANT_SUGGESTIONS,
  promptText,
  type AssistantPageSuggestions,
} from './pageSuggestions'
import SparkleIcon from './SparkleIcon'

interface AssistantEmptyStateProps {
  /** The prompt's text, and the case it is about when it names one. */
  onPick: (text: string, caseId?: string) => void
  disabled?: boolean
  /** Starter questions for the page the user is on. */
  suggestions?: AssistantPageSuggestions
  /** The nudge's "Needs you" card (narrow screens open the panel here). */
  needsYou?: ReactNode
}

const AssistantEmptyState = ({
  onPick,
  disabled,
  suggestions = DEFAULT_ASSISTANT_SUGGESTIONS,
  needsYou,
}: AssistantEmptyStateProps) => (
  <Box
    data-testid="assistant-empty-state"
    sx={{
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: 1.75,
      px: 2.5,
      py: 3,
      // Centre when there's room, scroll from the top when there isn't.
      '& > :first-of-type': { mt: 'auto' },
      '& > :last-child': { mb: 'auto' },
    }}
  >
    <SparkleIcon twoTone color="primary" sx={{ fontSize: 48 }} />
    <Typography variant="h6" component="h2" fontWeight={700}>
      How can I help today?
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 320 }}>
      Ask about your cases, find new ones, or get help with a referral.
      I&apos;ll always check with you before changing anything.
    </Typography>
    {needsYou && <Box sx={{ width: '100%', maxWidth: 360 }}>{needsYou}</Box>}
    <Box
      data-testid="assistant-suggested-for"
      sx={{
        bgcolor: 'primary.lightest',
        color: 'primary.main',
        typography: 'caption',
        fontWeight: 700,
        px: 1.25,
        py: 0.5,
        borderRadius: '60px',
      }}
    >
      Suggested for {suggestions.label}
    </Box>
    <Box
      component="ul"
      aria-label="Suggested questions"
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.25,
      }}
    >
      {suggestions.prompts.map(prompt => (
        <li key={promptText(prompt)}>
          <ButtonBase
            onClick={() =>
              typeof prompt === 'string'
                ? onPick(prompt)
                : onPick(prompt.text, prompt.caseId)
            }
            disabled={disabled}
            sx={{
              border: 1,
              borderColor: 'primary.main',
              color: 'primary.main',
              bgcolor: 'background.paper',
              typography: 'body2',
              px: 2.5,
              py: 1.25,
              borderRadius: '60px',
              textAlign: 'center',
              '&:hover, &.Mui-focusVisible': { bgcolor: 'primary.lightest' },
              '&.Mui-disabled': { opacity: 0.5 },
            }}
          >
            {promptText(prompt)}
          </ButtonBase>
        </li>
      ))}
    </Box>
  </Box>
)

export default AssistantEmptyState
